import {Component, computed, effect, inject, input, InputSignal, signal, Signal, WritableSignal} from '@angular/core';
import {ThirdPartyLicense, ThirdPartyLicenseService} from './third-party-license/third-party-license.service';
import {catchError, combineLatestWith, map, share, switchMap, take, tap} from 'rxjs/operators';
import {
  ThirdPartyLicenseOffering,
  ThirdPartyLicenseOfferingService
} from './third-party-license-offering/third-party-license-offering.service';
import {
  ThirdPartyLicenseManagement,
  ThirdPartyLicenseManagementAnswer,
  ThirdPartyLicenseManagementService,
  ThirdPartyLicenseManagementStatus
} from './third-party-license-management/third-party-license-management.service';
import {Observable, of, timer} from 'rxjs';
import {NotificationService} from '@uofu-uss/angular-util';
import {Messages, User, UserService} from '@uofu-uss-aux/software-catalog-ng-core';
import {NgIf} from '@angular/common';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';

enum LoadStatus {
  NotLoading,
  Loading,
  Loaded,
}

interface FallibleObject<T> {
  loaded: LoadStatus;
  object?: T;
}

type ValidatorFunction = () => boolean;

@Component({
  selector: 'app-third-party',
  standalone: true,
  imports: [
    NgIf,
  ],
  templateUrl: './third-party.component.html',
  styleUrl: './third-party.component.scss',
})
export class ThirdPartyComponent {

  private readonly thirdPartyLicenseService =
    inject(ThirdPartyLicenseService);
  private readonly thirdPartyLicenseOfferingService =
    inject(ThirdPartyLicenseOfferingService);
  private readonly thirdPartyLicenseManagementService =
    inject(ThirdPartyLicenseManagementService);
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationService);
  private readonly window = inject(Window);

  readonly vendorName: InputSignal<string> = input.required();
  readonly name: InputSignal<string> = input.required();
  readonly disableAuto: InputSignal<boolean | undefined> = input();
  readonly btnClasses: InputSignal<string | undefined> = input();
  readonly btnText: InputSignal<string | undefined> = input();
  readonly btnCaption: InputSignal<string | undefined> = input();
  readonly answers: InputSignal<ReadonlyArray<ThirdPartyLicenseManagementAnswer> | undefined> = input();
  readonly validator: InputSignal<ValidatorFunction | undefined> = input();

  private readonly vendorName$: Observable<string> = toObservable(this.vendorName);
  private readonly name$: Observable<string> = toObservable(this.name);
  private readonly thirdPartyLicense$: Observable<FallibleObject<ThirdPartyLicense>> = this.vendorName$.pipe(
    combineLatestWith(this.name$),
    switchMap(([vendorName, name]) => this.loadThirdPartyLicense(vendorName, name)),
    share()
  );
  private readonly thirdPartyLicense: Signal<FallibleObject<ThirdPartyLicense>> = toSignal(
    this.thirdPartyLicense$, {initialValue: {loaded: LoadStatus.Loading}}
  );

  private readonly thirdPartyLicenseOfferings$: Observable<FallibleObject<ReadonlyArray<ThirdPartyLicenseOffering>>> =
    this.thirdPartyLicense$.pipe(
      switchMap(license => (license.loaded === LoadStatus.Loaded && license.object)
        ? this.loadThirdPartyLicenseOfferings(license.object, this.userService.value.unid)
        : of({loaded: LoadStatus.Loaded, object: []})
      ),
    );
  private readonly thirdPartyLicenseOfferings: Signal<FallibleObject<ReadonlyArray<ThirdPartyLicenseOffering>>> =
    toSignal(this.thirdPartyLicenseOfferings$, {initialValue: {loaded: LoadStatus.Loading, object: []}});

  private readonly thirdPartyLicenseManagement: WritableSignal<FallibleObject<ThirdPartyLicenseManagement>> = signal(
    {loaded: LoadStatus.NotLoading}
  );

  private readonly managementStatus: Signal<ThirdPartyLicenseManagementStatus | undefined> = computed(() => {
    const management = this.thirdPartyLicenseManagement();
    return management.object?.status || undefined;
  });

  readonly loading: Signal<boolean> = computed(() => {
    const thirdPartyLicense = this.thirdPartyLicense();
    const thirdPartyLicenseOfferings = this.thirdPartyLicenseOfferings();
    const thirdPartyLicenseManagement = this.thirdPartyLicenseManagement();
    const isLoading = (o: FallibleObject<any>) => o.loaded === LoadStatus.Loading;
    return isLoading(thirdPartyLicense)
      || isLoading(thirdPartyLicenseOfferings)
      || isLoading(thirdPartyLicenseManagement);
  });

  readonly initialized: Signal<boolean> = computed(() => {
    const thirdPartyLicense = this.thirdPartyLicense();
    const thirdPartyLicenseOfferings = this.thirdPartyLicenseOfferings();
    const doneLoading = (o: FallibleObject<any>) => o.loaded !== LoadStatus.Loading;
    return doneLoading(thirdPartyLicense) && doneLoading(thirdPartyLicenseOfferings);
  });

  readonly allowedOffering: Signal<ThirdPartyLicenseOffering | undefined> = computed(() => {
    const offerings = this.thirdPartyLicenseOfferings();
    if (offerings.loaded === LoadStatus.Loaded && offerings.object && offerings.object.length) {
      return offerings.object[0];
    } else {
      return undefined;
    }
  });

  readonly auto: Signal<boolean> = computed(() => {
    const license = this.thirdPartyLicense();
    const disableAuto = this.disableAuto();
    return license.loaded === LoadStatus.Loaded
      && !!(license.object?.auto)
      && !disableAuto;
  });

  readonly managementPending: Signal<boolean> = computed(() => {
    const status = this.managementStatus();
    return status === ThirdPartyLicenseManagementStatus.Created;
  });

  readonly rateLimited: WritableSignal<boolean> = signal(false);

  readonly user: Signal<User> = signal(this.userService.value).asReadonly();
  readonly emplid: Signal<string> = computed(() => this.user().unid.replace('u', '0'));

  constructor() {
    effect(() => {
      const allowedOffering = this.allowedOffering();
      const auto = this.auto();
      const emplid = this.emplid();
      if (auto && allowedOffering) {
        this.activate(allowedOffering, emplid);
      }
    }, {allowSignalWrites: true});

    effect(() => {
      const license = this.thirdPartyLicense();
      const status = this.managementStatus();
      if (status === ThirdPartyLicenseManagementStatus.Synchronized) {
        const launchLink = license.object?.launchLink;
        if (launchLink) {
          this.window.location.assign(launchLink);
        } else {
          this.notificationService.addError('Unable to redirect to vendor website.');
        }
      } else if (status === ThirdPartyLicenseManagementStatus.Error) {
        this.notificationService.addError('Failed to activate license.');
      }
    });
  }

  loadThirdPartyLicense(vendorName: string,
                        name: string): Observable<FallibleObject<ThirdPartyLicense>> {
    return this.thirdPartyLicenseService
      .search(vendorName, name)
      .pipe(
        map(page => page.data[0]),
        map(license => ({loaded: LoadStatus.Loaded, object: license})),
        catchError((error: HttpErrorResponse) => {
          const message = Messages.validationMsg(error, 'Unable to load license information.');
          this.notificationService.addError(message);
          return of({loaded: LoadStatus.Loaded});
        }),
      );
  }

  loadThirdPartyLicenseOfferings(thirdPartyLicense: ThirdPartyLicense, unid: string):
    Observable<FallibleObject<ReadonlyArray<ThirdPartyLicenseOffering>>> {
    return this.thirdPartyLicenseOfferingService.search(thirdPartyLicense.id, unid).pipe(
      map(page => page.data),
      map(offerings => ({loaded: LoadStatus.Loaded, object: offerings})),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.addError(Messages.validationMsg(error, 'Unable to load license information.'));
        return of({loaded: LoadStatus.Loaded, object: []});
      }),
    );
  }

  activate(offering: ThirdPartyLicenseOffering, emplid: string): void {
    const validator = this.validator();
    const answers = this.answers() || [];
    if (validator && !validator()) {
      return;
    }

    this.thirdPartyLicenseManagement.set({loaded: LoadStatus.Loading});
    const observer = {
      next: (management: ThirdPartyLicenseManagement) => {
        this.thirdPartyLicenseManagement.set({loaded: LoadStatus.Loaded, object: management});
        this.rateLimited.set(false);
      },
      error: () => {
        this.thirdPartyLicenseManagement.set({loaded: LoadStatus.Loaded});
        this.notificationService.addError('Unable to activate license.');
        this.rateLimited.set(false);
      },
    };
    const observable = this.thirdPartyLicenseManagementService.createTplm({
      action: 'ACTIVATE',
      thirdPartyLicenseOfferingId: offering.id,
      emplid,
      answers,
    });
    observable.pipe(
      tap(created => this.thirdPartyLicenseManagement.set(
        {loaded: LoadStatus.Loading, object: created}
      )),
      tap(() => this.startRateLimitTimer()),
      switchMap(created =>
        this.thirdPartyLicenseManagementService.fetchUntilSynchronizedOrError(created.id)
      ),
      take(1)
    ).subscribe(observer);
  }

  startRateLimitTimer(): void {
    const tenSeconds = 10 * 1000;
    timer(tenSeconds)
      .pipe(take(1))
      .subscribe(() => this.rateLimited.set(true));
  }

}
